import React, { useMemo } from 'react';

import { gql } from '@apollo/client';

import {
  ActivityLogTaskRcAdjustInterviewerRsvp_ActivityLogFragment,
  ResponseStatus,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from '../../Helpers/ActivityLogBase';
import ActivityLogContent from '../../Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogTaskRcAdjustInterviewerRsvp_ActivityLogFragment;
}

const ActivityLogTaskRcAdjustInterviewerRsvp: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogRsvpDetails') {
    throw new Error('ActivityLogTaskRcAdjustInterviewerRsvp: details is not ActivityLogRsvpDetails');
  }

  const { interviewName, responseStatus, comment } = details;
  const actionLabel = useMemo(() => {
    switch (responseStatus) {
      case ResponseStatus.Accepted:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.adjustedRsvpAcceptedInterview',
          fallback: `adjusted an RSVP: ${details.rsvp?.fullName || ''} accepted ${interviewName}`,
        };
      case ResponseStatus.Declined:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.adjustedRsvpDeclinedInterview',
          fallback: `adjusted an RSVP: ${details.rsvp?.fullName || ''} declined ${interviewName}`,
        };
      case ResponseStatus.Tentative:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.adjustedRsvpTentativeInterview',
          fallback: `adjusted an RSVP: ${details.rsvp?.fullName || ''} responded maybe for ${interviewName}`,
        };
      default:
        return undefined;
    }
  }, [details.rsvp?.fullName, interviewName, responseStatus]);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabel={actionLabel?.fallback}
      actionLabelKey={actionLabel?.key}
      actionLabelValues={{
        name: details.rsvp?.fullName || '',
        interviewName,
      }}
    >
      {comment ? <ActivityLogContent content={comment} /> : null}
    </ActivityLogBase>
  );
};

ActivityLogTaskRcAdjustInterviewerRsvp.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogTaskRcAdjustInterviewerRsvp_activityLog on ActivityLog {
      id
      details {
        __typename
        ... on ActivityLogRsvpDetails {
          interviewName
          responseStatus
          comment
          rsvp: interviewer {
            fullName
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskRcAdjustInterviewerRsvp;
